// user-client/src/i18n/I18nProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import enBundle from './bundles/en';

const STORAGE_KEY = 'app_language';
const DEFAULT_LANG = 'en';
const HANGUL_RE = /[가-힣]/;

function safeFallbackForLanguage(language, key, fallback) {
  const value = fallback == null ? '' : String(fallback);
  if (!value) return String(key || '');
  const lang = normalizeI18nLanguage(language || DEFAULT_LANG);
  // Korean fallback strings are useful while developing Korean copy, but must never
  // leak into English/other-language UI when a key is accidentally missing.
  if (lang !== 'ko' && HANGUL_RE.test(value)) return String(key || '');
  return value;
}

// Keep this list in sync with src/state/languageState.js.
const SUPPORTED_LANGS = new Set([
  'en', 'ko', 'mn', 'ja', 'zh', 'zh-tw', 'vi', 'th', 'id', 'ms', 'tl', 'ru',
  'es', 'fr', 'de', 'pt', 'it', 'nl', 'pl', 'uk', 'hi', 'ar', 'tr', 'bn', 'ur', 'fa',
]);

// Loaded locale dictionaries only. The previous implementation imported all 364 JSON
// files into the initial bundle. Locale bundle modules are now split by language and
// loaded on demand (one chunk per language).
const LOCALE_LOADERS = {
  'en': () => import(/* webpackChunkName: "locale-en" */ './bundles/en.js'),
  'ko': () => import(/* webpackChunkName: "locale-ko" */ './bundles/ko.js'),
  'mn': () => import(/* webpackChunkName: "locale-mn" */ './bundles/mn.js'),
  'ja': () => import(/* webpackChunkName: "locale-ja" */ './bundles/ja.js'),
  'zh': () => import(/* webpackChunkName: "locale-zh" */ './bundles/zh.js'),
  'zh-tw': () => import(/* webpackChunkName: "locale-zh-tw-canonical" */ './bundles/zh-tw-canonical.js'),
  'vi': () => import(/* webpackChunkName: "locale-vi" */ './bundles/vi.js'),
  'th': () => import(/* webpackChunkName: "locale-th" */ './bundles/th.js'),
  'id': () => import(/* webpackChunkName: "locale-id" */ './bundles/id.js'),
  'ms': () => import(/* webpackChunkName: "locale-ms" */ './bundles/ms.js'),
  'tl': () => import(/* webpackChunkName: "locale-tl" */ './bundles/tl.js'),
  'ru': () => import(/* webpackChunkName: "locale-ru" */ './bundles/ru.js'),
  'es': () => import(/* webpackChunkName: "locale-es" */ './bundles/es.js'),
  'fr': () => import(/* webpackChunkName: "locale-fr" */ './bundles/fr.js'),
  'de': () => import(/* webpackChunkName: "locale-de" */ './bundles/de.js'),
  'pt': () => import(/* webpackChunkName: "locale-pt" */ './bundles/pt.js'),
  'it': () => import(/* webpackChunkName: "locale-it" */ './bundles/it.js'),
  'nl': () => import(/* webpackChunkName: "locale-nl" */ './bundles/nl.js'),
  'pl': () => import(/* webpackChunkName: "locale-pl" */ './bundles/pl.js'),
  'uk': () => import(/* webpackChunkName: "locale-uk" */ './bundles/uk.js'),
  'hi': () => import(/* webpackChunkName: "locale-hi" */ './bundles/hi.js'),
  'ar': () => import(/* webpackChunkName: "locale-ar" */ './bundles/ar.js'),
  'tr': () => import(/* webpackChunkName: "locale-tr" */ './bundles/tr.js'),
  'bn': () => import(/* webpackChunkName: "locale-bn" */ './bundles/bn.js'),
  'ur': () => import(/* webpackChunkName: "locale-ur" */ './bundles/ur.js'),
  'fa': () => import(/* webpackChunkName: "locale-fa" */ './bundles/fa.js'),
};

const __LOCALES__ = Object.create(null);
__LOCALES__[DEFAULT_LANG] = enBundle;
const __PACKAGED_LOCALES_LOADED__ = new Set([DEFAULT_LANG]);
const __LOCALE_LOADS__ = new Map();

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(target, source) {
  if (!isPlainObject(target) || !isPlainObject(source)) return target;
  for (const k of Object.keys(source)) {
    const sv = source[k];
    const tv = target[k];
    if (isPlainObject(sv) && isPlainObject(tv)) target[k] = deepMerge({ ...tv }, sv);
    else target[k] = sv;
  }
  return target;
}

function getByPath(obj, path) {
  if (!obj) return undefined;
  const parts = String(path || '').split('.').filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

function interpolate(str, vars) {
  if (!vars || typeof vars !== 'object') return str;
  return String(str).replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined || v === null ? '' : String(v);
  });
}

function readStoredLanguage() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    const s = String(v || '').trim();
    return s || '';
  } catch (_) {
    return '';
  }
}

function writeStoredLanguage(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, String(lang || DEFAULT_LANG));
  } catch (_) {}
}

export function normalizeI18nLanguage(lang) {
  let s = String(lang || '').trim().replace(/_/g, '-').toLowerCase();
  if (!s) return DEFAULT_LANG;

  if (s === 'kr' || s.startsWith('ko')) return 'ko';
  if (s === 'fil' || s === 'ph' || s.startsWith('fil-')) return 'tl';
  if (s === 'zh-tw' || s === 'zh-hant' || s === 'zh-hk' || s === 'zh-mo') return 'zh-tw';
  if (s === 'zh-cn' || s === 'zh-sg' || s === 'zh-hans') return 'zh';
  if (s === 'pt-br' || s === 'pt-pt') return 'pt';
  if (s === 'es-mx' || s === 'es-es') return 'es';

  if (SUPPORTED_LANGS.has(s)) return s;
  const base = s.split('-')[0];
  if (SUPPORTED_LANGS.has(base)) return base;
  return DEFAULT_LANG;
}

function mergeLoadedLocale(language, dict) {
  const lang = normalizeI18nLanguage(language);
  const prev = __LOCALES__[lang];
  // Runtime registrations are allowed to override packaged translations, so any
  // dictionary that was registered before the chunk finished loading wins last.
  __LOCALES__[lang] = prev
    ? deepMerge({ ...(isPlainObject(dict) ? dict : {}) }, prev)
    : (isPlainObject(dict) ? dict : {});
  __PACKAGED_LOCALES_LOADED__.add(lang);
  return __LOCALES__[lang];
}

export function isLocaleLoaded(language) {
  const lang = normalizeI18nLanguage(language);
  return __PACKAGED_LOCALES_LOADED__.has(lang);
}

export function ensureLocaleLoaded(language) {
  const lang = normalizeI18nLanguage(language);
  if (__PACKAGED_LOCALES_LOADED__.has(lang)) return Promise.resolve(__LOCALES__[lang] || {});

  const existing = __LOCALE_LOADS__.get(lang);
  if (existing) return existing;

  const loader = LOCALE_LOADERS[lang] || LOCALE_LOADERS[DEFAULT_LANG];
  const load = loader()
    .then((mod) => mergeLoadedLocale(lang, mod?.default || mod || {}))
    .catch((error) => {
      __LOCALE_LOADS__.delete(lang);
      throw error;
    });

  __LOCALE_LOADS__.set(lang, load);
  return load;
}

function ensureLanguagePair(language) {
  const lang = normalizeI18nLanguage(language);
  if (lang === DEFAULT_LANG) return ensureLocaleLoaded(DEFAULT_LANG);
  return Promise.all([ensureLocaleLoaded(DEFAULT_LANG), ensureLocaleLoaded(lang)]).then(() => __LOCALES__[lang]);
}

// Called once before React mounts so the first painted screen already has the same
// translations as before, while still keeping all unrelated languages out of the
// initial JavaScript bundle.
export async function preloadInitialTranslations(defaultLang = DEFAULT_LANG) {
  const stored = readStoredLanguage();
  const lang = normalizeI18nLanguage(stored || defaultLang || DEFAULT_LANG);
  try {
    await ensureLanguagePair(lang);
  } catch (error) {
    // English is the final packaged fallback. If a selected locale chunk failed,
    // still try to make the application usable with English rather than blocking mount.
    try { await ensureLocaleLoaded(DEFAULT_LANG); } catch (_) {}
    if (process.env.NODE_ENV !== 'production') {
      try { console.warn('[i18n] initial locale load failed', lang, error?.message || error); } catch (_) {}
    }
  }
  return lang;
}

const I18nContext = createContext(null);

export function I18nProvider({ children, defaultLang = DEFAULT_LANG }) {
  const initial = useMemo(() => {
    const stored = readStoredLanguage();
    return normalizeI18nLanguage(stored || defaultLang || DEFAULT_LANG);
  }, [defaultLang]);

  const [lang, _setLang] = useState(initial);
  const [revision, setRevision] = useState(0);
  const languageRequestSeqRef = useRef(0);

  const loadAndApplyLanguage = useCallback((raw, { persist = false, broadcast = false } = {}) => {
    const next = normalizeI18nLanguage(raw || defaultLang || DEFAULT_LANG);
    const seq = ++languageRequestSeqRef.current;

    return ensureLanguagePair(next)
      .catch((error) => {
        if (process.env.NODE_ENV !== 'production') {
          try { console.warn('[i18n] locale load failed', next, error?.message || error); } catch (_) {}
        }
        return null;
      })
      .then(() => {
        if (seq !== languageRequestSeqRef.current) return next;
        _setLang((prev) => (prev === next ? prev : next));
        setRevision((r) => r + 1);

        if (persist) writeStoredLanguage(next);
        if (broadcast) {
          try {
            window.dispatchEvent(new CustomEvent('APP_LANGUAGE_CHANGED', {
              detail: { language: next },
            }));
          } catch (_) {}
        }
        return next;
      });
  }, [defaultLang]);

  // Safety net for tests/embedded entry points that mount the provider without going
  // through index.js preloadInitialTranslations().
  useEffect(() => {
    let alive = true;
    ensureLanguagePair(lang)
      .then(() => { if (alive) setRevision((r) => r + 1); })
      .catch(() => {});
    return () => { alive = false; };
  }, [lang]);

  useEffect(() => {
    const syncExternalLanguage = (event) => {
      if (event?.type === 'storage' && event?.key && event.key !== STORAGE_KEY) return;
      const raw = event?.detail?.language || event?.newValue || readStoredLanguage() || defaultLang;
      void loadAndApplyLanguage(raw, { persist: false, broadcast: false });
    };

    window.addEventListener('APP_LANGUAGE_CHANGED', syncExternalLanguage);
    window.addEventListener('storage', syncExternalLanguage);
    return () => {
      window.removeEventListener('APP_LANGUAGE_CHANGED', syncExternalLanguage);
      window.removeEventListener('storage', syncExternalLanguage);
    };
  }, [defaultLang, loadAndApplyLanguage]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    document.documentElement.dir = ['ar', 'fa', 'ur'].includes(lang) ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = useCallback((next) => {
    return loadAndApplyLanguage(next, { persist: true, broadcast: true });
  }, [loadAndApplyLanguage]);

  const registerTranslations = useCallback((language, dict) => {
    const l = normalizeI18nLanguage(language);
    if (!__LOCALES__[l]) __LOCALES__[l] = {};
    if (isPlainObject(dict)) {
      __LOCALES__[l] = deepMerge({ ...__LOCALES__[l] }, dict);
      setRevision((r) => r + 1);
    }
  }, []);

  const t = useCallback((key, vars, optOrFallback) => {
    const k = String(key || '').trim();
    if (!k) return '';

    const opt = isPlainObject(optOrFallback)
      ? optOrFallback
      : (optOrFallback !== undefined && optOrFallback !== null
        ? { fallback: String(optOrFallback) }
        : {});

    const dict = __LOCALES__[lang] || {};
    let val = Object.prototype.hasOwnProperty.call(dict, k) ? dict[k] : getByPath(dict, k);

    if (val === undefined) {
      const enDict = __LOCALES__.en || {};
      val = Object.prototype.hasOwnProperty.call(enDict, k) ? enDict[k] : getByPath(enDict, k);
    }

    if (val === undefined) val = safeFallbackForLanguage(lang, k, opt?.fallback ?? k);
    const s = typeof val === 'string' ? val : String(val);
    return interpolate(s, vars);
  }, [lang, revision]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t,
    registerTranslations,
  }), [lang, setLang, t, registerTranslations]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within <I18nProvider>');
  return ctx;
}

export function useOptionalI18n() {
  const ctx = useContext(I18nContext);
  const fallbackT = useCallback((key, vars, fallback) => translateNow(key, vars, fallback), []);

  return useMemo(() => {
    const baseT = typeof ctx?.t === 'function' ? ctx.t : fallbackT;
    const optional = (key, vars, fallback) => baseT(key, vars, fallback);
    return Object.assign(optional, ctx || {}, { t: baseT });
  }, [ctx, fallbackT]);
}

export function translateNow(key, vars, fallback) {
  const k = String(key || '').trim();
  if (!k) return String(fallback ?? '');
  const langNow = getStoredLanguage();
  const dict = __LOCALES__[langNow] || {};
  const enDict = __LOCALES__.en || {};
  let val = Object.prototype.hasOwnProperty.call(dict, k) ? dict[k] : getByPath(dict, k);
  if (val === undefined) {
    val = Object.prototype.hasOwnProperty.call(enDict, k) ? enDict[k] : getByPath(enDict, k);
  }
  if (val === undefined) val = safeFallbackForLanguage(langNow, k, fallback ?? k);
  return interpolate(typeof val === 'string' ? val : String(val), vars);
}

export function getStoredLanguage() {
  const stored = readStoredLanguage();
  return normalizeI18nLanguage(stored || DEFAULT_LANG);
}
